import { useEffect, useState, useCallback } from "react";
import { motion } from "framer-motion";
import { Plus, Loader2, Trash2, X, FolderKanban, ChevronRight } from "lucide-react";
import api from "../lib/apiClient";
import { XpBar } from "../components/XpBar";
import { fmtDate, PROJECT_TYPE_META, STATUS_COLORS, RISK_META } from "../lib/gameData";
import { toast } from "sonner";

const STATUSES = ["En cours", "À faire", "Terminée"];

export default function Projects() {
  const [projects, setProjects] = useState(null);
  const [config, setConfig] = useState(null);
  const [open, setOpen] = useState(false);
  const [detail, setDetail] = useState(null);

  const load = useCallback(async () => {
    const [p, c] = await Promise.all([api.get("/projects"), api.get("/config")]);
    setProjects(p.data);
    setConfig(c.data);
  }, []);

  useEffect(() => { load(); }, [load]);

  const openDetail = async (id) => {
    const { data } = await api.get(`/projects/${id}`);
    setDetail(data);
  };

  const updateStatus = async (id, status) => {
    const { data } = await api.put(`/projects/${id}`, { status });
    if (data.xp_awarded) toast.success(`Projet terminé · +${data.xp_awarded} XP`);
    await load();
    if (detail?.id === id) openDetail(id);
  };

  const del = async (id) => {
    await api.delete(`/projects/${id}`);
    toast.success("Projet supprimé");
    setDetail(null);
    load();
  };

  if (!projects) return <div className="flex justify-center py-20"><Loader2 className="w-7 h-7 text-amber-500 animate-spin" /></div>;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-3xl font-extrabold">Mes projets</h1>
          <p className="text-zinc-500 text-sm mt-1">Transforme ton objectif en réalisations.</p>
        </div>
        <button onClick={() => setOpen(true)} data-testid="new-project-btn"
          className="inline-flex items-center gap-2 px-4 h-11 rounded-xl gold-gradient text-black font-semibold hover:opacity-90 transition-opacity">
          <Plus className="w-5 h-5" /> <span className="hidden sm:inline">Nouveau projet</span>
        </button>
      </div>

      {projects.length === 0 ? (
        <div className="bg-[#18181E] border border-zinc-800 rounded-3xl p-12 text-center" data-testid="projects-empty">
          <FolderKanban className="w-10 h-10 text-zinc-600 mx-auto mb-3" />
          <p className="text-zinc-400">Aucun projet pour l'instant.</p>
          <button onClick={() => setOpen(true)} className="mt-4 text-amber-400 font-medium hover:underline">Créer mon premier projet</button>
        </div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="projects-grid">
          {projects.map((p) => (
            <motion.button key={p.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
              onClick={() => openDetail(p.id)} data-testid={`project-card-${p.id}`}
              className="text-left bg-[#18181E] border border-zinc-800 rounded-3xl p-5 card-hover relative">
              <div className="flex items-start justify-between mb-3">
                <span className="text-2xl">{PROJECT_TYPE_META[p.type] || "✨"}</span>
                <span className={`text-xs px-2.5 py-1 rounded-full border ${STATUS_COLORS[p.status] || ""}`}>{p.status}</span>
              </div>
              <h3 className="font-display text-lg font-bold">{p.title}</h3>
              <p className="text-xs text-zinc-500 mt-0.5">{p.type} · {p.task_count} tâche{p.task_count > 1 ? "s" : ""}</p>
              {p.description && <p className="text-sm text-zinc-400 mt-2 line-clamp-2">{p.description}</p>}
              <div className="mt-4 flex items-center gap-3">
                <XpBar value={p.progress} max={100} className="flex-1 h-2" />
                <span className="font-mono text-xs text-amber-400">{p.progress}%</span>
              </div>
              <div className="mt-3 flex items-center justify-between text-xs text-zinc-600">
                <span>Cible : {fmtDate(p.target_date)}</span>
                <div className="flex items-center gap-2">
                  {p.phase && p.status !== "Terminée" && <span className="font-mono text-amber-500/80">{p.phase}</span>}
                  {p.risk && p.status !== "Terminée" && (
                    <span className="flex items-center gap-1" style={{ color: RISK_META[p.risk].color }} data-testid={`risk-${p.id}`}>
                      {RISK_META[p.risk].dot}
                    </span>
                  )}
                  <ChevronRight className="w-4 h-4" />
                </div>
              </div>
            </motion.button>
          ))}
        </div>
      )}

      {open && <NewProjectModal config={config} onClose={() => setOpen(false)} onCreated={() => { setOpen(false); load(); }} />}
      {detail && <ProjectDetail project={detail} onClose={() => setDetail(null)} onStatus={updateStatus} onDelete={del} />}
    </div>
  );
}

function NewProjectModal({ config, onClose, onCreated }) {
  const [form, setForm] = useState({ title: "", type: config.project_types[0], description: "", start_date: "", target_date: "", status: "En cours" });
  const [loading, setLoading] = useState(false);
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/projects", form);
      toast.success("Projet créé !");
      (data.new_badges || []).forEach(() => toast.success("🏅 Nouveau badge débloqué !"));
      onCreated();
    } finally { setLoading(false); }
  };

  return (
    <Modal onClose={onClose} title="Nouveau projet" testid="new-project-modal">
      <form onSubmit={submit} className="space-y-4">
        <Input label="Titre" value={form.title} onChange={set("title")} testid="project-title" required />
        <div className="grid grid-cols-2 gap-4">
          <Select label="Type" value={form.type} onChange={set("type")} options={config.project_types} testid="project-type" />
          <Select label="Statut" value={form.status} onChange={set("status")} options={STATUSES} testid="project-status" />
        </div>
        <div>
          <label className="block text-xs font-medium text-zinc-400 mb-1.5">Description</label>
          <textarea value={form.description} onChange={(e) => set("description")(e.target.value)} rows={2} data-testid="project-description"
            className="w-full px-4 py-3 rounded-xl bg-zinc-900 border border-zinc-800 text-zinc-100 focus:border-amber-500 outline-none resize-none" />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <Input label="Date de début" type="date" value={form.start_date} onChange={set("start_date")} testid="project-start" />
          <Input label="Date cible" type="date" value={form.target_date} onChange={set("target_date")} testid="project-target" />
        </div>
        <button type="submit" disabled={loading} data-testid="project-submit"
          className="w-full h-12 rounded-xl gold-gradient text-black font-semibold flex items-center justify-center hover:opacity-90 disabled:opacity-60">
          {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : "Créer le projet"}
        </button>
      </form>
    </Modal>
  );
}

function ProjectDetail({ project, onClose, onStatus, onDelete }) {
  return (
    <Modal onClose={onClose} title={project.title} testid="project-detail-modal">
      <div className="space-y-4">
        <div className="flex items-center gap-2">
          <span className="text-2xl">{PROJECT_TYPE_META[project.type] || "✨"}</span>
          <span className={`text-xs px-2.5 py-1 rounded-full border ${STATUS_COLORS[project.status] || ""}`}>{project.status}</span>
        </div>
        {project.description && <p className="text-sm text-zinc-400">{project.description}</p>}
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div><p className="text-zinc-500 text-xs">Début</p><p>{fmtDate(project.start_date)}</p></div>
          <div><p className="text-zinc-500 text-xs">Cible</p><p>{fmtDate(project.target_date)}</p></div>
        </div>
        <div>
          <div className="flex justify-between text-sm mb-2"><span className="text-zinc-400">Progression</span><span className="font-mono text-amber-400">{project.progress}%</span></div>
          <XpBar value={project.progress} max={100} className="h-2" />
        </div>
        <div>
          <p className="text-xs font-mono uppercase tracking-wider text-amber-500 mb-2">Tâches ({project.tasks.length})</p>
          {project.tasks.length === 0 ? <p className="text-sm text-zinc-500">Aucune tâche. Ajoute-en depuis l'onglet Tâches.</p> : (
            <div className="space-y-2">
              {project.tasks.map((t) => (
                <div key={t.id} className="flex items-center justify-between bg-zinc-900 rounded-xl px-3 py-2 text-sm">
                  <span className={t.status === "Terminée" ? "line-through text-zinc-500" : ""}>{t.title}</span>
                  <span className={`text-xs px-2 py-0.5 rounded-full border ${STATUS_COLORS[t.status] || ""}`}>{t.status}</span>
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="flex flex-wrap gap-2 pt-2">
          {STATUSES.map((s) => (
            <button key={s} onClick={() => onStatus(project.id, s)} data-testid={`set-status-${s}`}
              className={`px-3 h-9 rounded-lg text-xs font-medium border transition-colors ${project.status === s ? "gold-gradient text-black border-transparent" : "border-zinc-700 text-zinc-300 hover:border-amber-500"}`}>
              {s}
            </button>
          ))}
          <button onClick={() => onDelete(project.id)} data-testid="delete-project"
            className="ml-auto px-3 h-9 rounded-lg text-xs font-medium border border-red-500/30 text-red-400 hover:bg-red-500/10 inline-flex items-center gap-1.5">
            <Trash2 className="w-4 h-4" /> Supprimer
          </button>
        </div>
      </div>
    </Modal>
  );
}

export function Modal({ children, onClose, title, testid }) {
  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4" data-testid={testid}>
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }}
        className="relative w-full sm:max-w-lg bg-[#18181E] border border-zinc-800 rounded-t-3xl sm:rounded-3xl p-6 max-h-[92vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-5">
          <h2 className="font-display text-xl font-bold pr-8">{title}</h2>
          <button onClick={onClose} data-testid="modal-close" className="text-zinc-500 hover:text-zinc-200"><X className="w-5 h-5" /></button>
        </div>
        {children}
      </motion.div>
    </div>
  );
}

export function Input({ label, type = "text", value, onChange, testid, required }) {
  return (
    <div>
      <label className="block text-xs font-medium text-zinc-400 mb-1.5">{label}</label>
      <input type={type} value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid} required={required}
        className="w-full h-12 px-4 rounded-xl bg-zinc-900 border border-zinc-800 text-zinc-100 focus:border-amber-500 outline-none [color-scheme:dark]" />
    </div>
  );
}

export function Select({ label, value, onChange, options, testid }) {
  return (
    <div>
      <label className="block text-xs font-medium text-zinc-400 mb-1.5">{label}</label>
      <select value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid}
        className="w-full h-12 px-4 rounded-xl bg-zinc-900 border border-zinc-800 text-zinc-100 focus:border-amber-500 outline-none">
        {options.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    </div>
  );
}
